import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Modalities and specializations are stored as keys (EXERCISE_TOOLS); the labels are i18n
 * resources under `enums.*` in the therapists namespace. Five unrelated pages display these
 * values, so the label lives in the locale files rather than in a prop each page would have
 * to be handed.
 *
 * A hook rather than a bare function so every call site loads the `therapists` namespace and
 * re-renders when the admin switches language -- reading the i18n singleton directly would
 * silently return raw keys on a page that never requested the namespace.
 *
 * Anything the catalogue does not know falls back to the stored string itself. That is not a
 * nicety: thousands of rows predate the closed lists and still hold free text ("stetoskop",
 * "belum ada"), and a blank badge would read as missing data rather than legacy data.
 */
export const useTherapistEnumLabel = () => {
	const { t } = useTranslation("therapists");

	return useCallback(
		(group: "modality" | "specialization", value: string): string =>
			t(`enums.${group}.${value}`, { defaultValue: value }),
		[t],
	);
};
